import React, { useState, useEffect, useCallback, useMemo } from 'react'
import api from '../services/api'
import toast from 'react-hot-toast'
import { useIsMobile } from '../hooks/useIsMobile'
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  DollarSign,
  Download,
  Filter,
  RefreshCw,
  Search,
  TrendingUp,
  XCircle,
} from 'lucide-react'
import { format, parseISO, subDays } from 'date-fns'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts'
import SettlementNoticeBanner from '../components/SettlementNoticeBanner'

// ─────────────────────────────────────────────────────────────────────────────
//   ADMIN LEADS DASHBOARD
//   Vista de ADMIN sobre TODOS los leads (todos los orígenes: partners,
//   orgánicos y sin identificar). Consume /core/admin/leads* gateado por
//   require_admin en el backend.
//
//   Espeja el dashboard del partner (mismos charts/filtros/CSV) y agrega:
//     - Columna ORIGEN por lead (qué partner, "Organic" o "Unidentified").
//     - Selector de origen (todos los partners + Organic + Unidentified).
//     - Búsqueda por kickoff_lead_id.
//     - Toggle para incluir leads de prueba.
//     - Desglose por origen.
// ─────────────────────────────────────────────────────────────────────────────

const POLL_MS = 15000          // refresca cada 15s (vista pesada, todos los leads)
// Ciclo propio para winning-bid: la agregación sobre auctionLogs es pesada y el
// dato no cambia cada 15s. Se refresca igual al aplicar filtros y en Refresh.
const WINNING_BID_POLL_MS = 60000
const PAGE_SIZE = 50
const EXPORT_PAGE_LIMIT = 200
const EXPORT_MAX_PAGES = 50    // tope de seguridad (50 * 200 = 10k leads)

// Mapping del estado que devuelve el backend → cómo se muestra.
const STATUS_META = {
  sold: {
    label: 'Sold',
    color: 'var(--green)',
    bg: 'var(--green-bg)',
    icon: <DollarSign size={11} />,
  },
  accepted: {
    label: 'Accepted',
    color: 'var(--amber)',
    bg: 'var(--bg-raised)',
    icon: <CheckCircle2 size={11} />,
  },
  rejected: {
    label: 'Rejected',
    color: 'var(--red)',
    bg: 'var(--red-bg)',
    icon: <XCircle size={11} />,
  },
}

function getStatusMeta(status) {
  return STATUS_META[status] || {
    label: status || '—',
    color: 'var(--text-muted)',
    bg: 'var(--bg-raised)',
    icon: null,
  }
}

// Helpers de formato
const fmtDateTime = (ts) => {
  if (!ts) return '—'
  try { return format(parseISO(ts), 'dd/MM HH:mm:ss') } catch { return '—' }
}
const fmtDayLabel = (d) => {
  if (!d) return ''
  try { return format(parseISO(d), 'dd/MM') } catch { return d }
}
const fmtMoney = (v) =>
  (v === null || v === undefined || v === '') ? '—' : `$${Number(v).toFixed(2)}`

// Estilo común para los tooltips de recharts (tema oscuro/claro vía CSS vars)
const TOOLTIP_STYLE = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 11,
  color: 'var(--text-primary)',
  boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
}
const TOOLTIP_LABEL_STYLE = { color: 'var(--text-secondary)', fontSize: 10 }

// ─────────────────────────────────────────────────────────────────────────────
//   EXPORTACIÓN CSV  (incluye la columna de ORIGEN)
// ─────────────────────────────────────────────────────────────────────────────
const CSV_HEADERS = [
  'Lead ID', 'Origin', 'Status', 'Buyer', 'Postback Value', 'Our Share', 'Partner Share',
  'Received At', 'Rejection reason', 'Rejection detail',
]

const csvMoney = (v) =>
  (v !== null && v !== undefined) ? Number(v).toFixed(2) : ''

function leadToCsvCells(l) {
  return [
    l.kickoff_lead_id || '',
    l.origin || l.partner || '',
    l.partner_status || '',
    l.winner_lender || '',
    csvMoney(l.front_end_value),
    csvMoney(l.our_value),
    csvMoney(l.partner_value),
    fmtCsvDate(l.createdAt),
    l.rejection_reason || '',
    l.rejection_detail || '',
  ]
}

const fmtCsvDate = (ts) => {
  if (!ts) return ''
  try { return format(parseISO(ts), 'yyyy-MM-dd HH:mm:ss') } catch { return ts }
}

function csvEscape(v) {
  if (v === null || v === undefined) return ''
  const s = String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function buildCsv(rows) {
  const header = CSV_HEADERS.map(csvEscape).join(',')
  const body = rows.map(r => leadToCsvCells(r).map(csvEscape).join(',')).join('\n')
  return `${header}\n${body}`
}

function downloadCsv(csv, filename) {
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ════════════════════════════════════════════════════════════════════════════
//   STATUS BADGE
// ════════════════════════════════════════════════════════════════════════════
function StatusBadge({ status }) {
  const meta = getStatusMeta(status)
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
      padding: '4px 9px', borderRadius: 4,
      background: meta.bg,
      color: meta.color,
      border: `1px solid ${meta.color}30`,
      fontFamily: 'ui-monospace, monospace',
    }}>
      {meta.icon}
      {meta.label}
    </span>
  )
}

// Etiqueta de origen. "Unidentified" se muestra atenuado; los demás en color normal.
function OriginBadge({ origin }) {
  const isUnknown = !origin || origin === 'Unidentified'
  return (
    <span style={{
      fontSize: 11, fontWeight: 600,
      color: isUnknown ? 'var(--text-muted)' : 'var(--text-secondary)',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      fontStyle: isUnknown ? 'italic' : 'normal',
    }} title={origin || 'Unidentified'}>
      {origin || 'Unidentified'}
    </span>
  )
}

// Comprador (lender ganador del lead). Vacío -> guion atenuado.
function BuyerBadge({ lender }) {
  if (!lender) {
    return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 600, color: 'var(--text-primary)',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      fontFamily: 'ui-monospace, monospace',
    }} title={lender}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent, var(--blue))', flexShrink: 0 }} />
      {lender}
    </span>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   STATS STRIP
// ════════════════════════════════════════════════════════════════════════════
function StatsStrip({ summary }) {
  const isMobile = useIsMobile()  
  const loading = !summary
  const by = summary?.by_status || {}

  const cells = [
    { key: 'total',    label: 'Total',    val: summary?.total,   color: 'var(--text-primary)' },
    { key: 'sold',     label: 'Sold',     val: by.sold || 0,     color: 'var(--green)' },
    { key: 'accepted', label: 'Accepted', val: by.accepted || 0, color: 'var(--amber)' },
    { key: 'rejected', label: 'Rejected', val: by.rejected || 0, color: 'var(--red)' },
  ]

  const acceptance = summary?.acceptance_pct ?? 0

  if (isMobile) {
    const stat = (key, val, label, color) => (
      <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          fontFamily: 'ui-monospace, monospace', fontSize: 22, fontWeight: 800,
          color, opacity: loading ? 0.4 : 1, lineHeight: 1.1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{loading ? '—' : (val ?? 0).toLocaleString()}</span>
        <span style={{
          fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase',
          letterSpacing: 0.6, fontWeight: 600,
        }}>{label}</span>
      </div>
    )

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 14,
        padding: '16px 18px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {cells.map(c => stat(c.key, c.val, c.label, c.color))}
        </div>
        <div style={{ height: 1, background: 'var(--border)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <TrendingUp size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Acceptance rate</span>
          <span style={{
            marginLeft: 'auto', fontFamily: 'ui-monospace, monospace', fontSize: 22, fontWeight: 800,
            opacity: loading ? 0.4 : 1,
            color: acceptance > 50 ? 'var(--green)' : acceptance > 30 ? 'var(--amber)' : 'var(--red)',
          }}>{loading ? '—' : `${acceptance}%`}</span>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex', gap: 28, alignItems: 'center',
      padding: '16px 24px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      flexWrap: 'wrap',
    }}>
      {cells.map((c, i) => (
        <React.Fragment key={c.key}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 26, fontWeight: 800, color: c.color,
              opacity: loading ? 0.4 : 1,
            }}>
              {loading ? '—' : (c.val ?? 0).toLocaleString()}
            </span>
            <span style={{
              fontSize: 10, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600,
            }}>
              {c.label}
            </span>
          </div>
          {i < cells.length - 1 && (
            <span style={{ color: 'var(--border-strong)', opacity: 0.4 }}>|</span>
          )}
        </React.Fragment>
      ))}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
        <TrendingUp size={14} style={{ color: 'var(--text-muted)' }} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Acceptance rate
        </span>
        <span style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: 22, fontWeight: 800,
          opacity: loading ? 0.4 : 1,
          color: acceptance > 50 ? 'var(--green)'
               : acceptance > 30 ? 'var(--amber)'
               : 'var(--red)',
        }}>
          {loading ? '—' : `${acceptance}%`}
        </span>
      </div>
    </div>
  )
}

// Formato de dinero con separador de miles para los KPIs.
const fmtMoneyBig = (v) =>
  (v === null || v === undefined)
    ? '$0.00'
    : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// ════════════════════════════════════════════════════════════════════════════
//   REVENUE STRIP  (totales de dinero sobre el filtro: solo leads SOLD)
// ════════════════════════════════════════════════════════════════════════════
function RevenueStrip({ summary }) {
  const isMobile = useIsMobile()  
  const loading = !summary
  const rev = summary?.revenue || {}

  const cells = [
    { key: 'postback', label: 'Postback total', val: rev.postback_total, color: 'var(--text-primary)' },
    { key: 'our',      label: 'Our share',      val: rev.our_total,      color: 'var(--green)' },
    { key: 'partner',  label: 'Partners share', val: rev.partner_total,  color: 'var(--amber)' },
  ]

  if (isMobile) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 12,
        padding: '16px 18px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <span style={{
          fontSize: 10, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700,
        }}>Revenue</span>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cells.map(c => (
            <div key={c.key} style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{
                fontFamily: 'ui-monospace, monospace', fontSize: 21, fontWeight: 800,
                color: c.color, opacity: loading ? 0.4 : 1,
              }}>{loading ? '—' : fmtMoneyBig(c.val)}</span>
              <span style={{
                fontSize: 10, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600,
              }}>{c.label}</span>
            </div>
          ))}
        </div>

        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
          based on sold leads in the current filter
        </span>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex', gap: 28, alignItems: 'center',
      padding: '16px 24px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      flexWrap: 'wrap',
    }}>
      <span style={{
        fontSize: 10, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700,
      }}>
        Revenue
      </span>
      <span style={{ color: 'var(--border-strong)', opacity: 0.4 }}>|</span>
      {cells.map((c, i) => (
        <React.Fragment key={c.key}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 24, fontWeight: 800, color: c.color,
              opacity: loading ? 0.4 : 1,
            }}>
              {loading ? '—' : fmtMoneyBig(c.val)}
            </span>
            <span style={{
              fontSize: 10, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600,
            }}>
              {c.label}
            </span>
          </div>
          {i < cells.length - 1 && (
            <span style={{ color: 'var(--border-strong)', opacity: 0.4 }}>|</span>
          )}
        </React.Fragment>
      ))}
      <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-muted)' }}>
        based on sold leads in the current filter
      </span>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   FILTROS  (origen + estado + búsqueda + fechas + tests + export + refresh)
// ════════════════════════════════════════════════════════════════════════════
const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d',    label: '7 days' },
  { id: '30d',   label: '30 days' },
  { id: 'ytd',   label: 'YTD' },
]


const dateInputStyle = {
  padding: '5px 8px', fontSize: 12, borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-surface)',
  color: 'var(--text-primary)', cursor: 'pointer',
  fontFamily: 'ui-monospace, monospace',
  colorScheme: 'dark light',
}

const selectStyle = {
  padding: '6px 10px', fontSize: 12, borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-surface)',
  color: 'var(--text-primary)', cursor: 'pointer',
}

function PresetButton({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 11px', fontSize: 11, fontWeight: 600,
        borderRadius: 6, cursor: 'pointer',
        background: active ? 'var(--bg-raised)' : 'transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-muted)',
        border: `1px solid ${active ? 'var(--border-strong)' : 'var(--border)'}`,
        transition: 'all .12s',
      }}
    >
      {label}
    </button>
  )
}

// Multi-select con chips: dropdown de opciones (checkbox) + tags removibles.
function MultiChipSelect({ label, options, selected, onChange, width }) {
  const [open, setOpen] = useState(false)
  const ref = React.useRef(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val])
  }
  const labelFor = (val) => (options.find(o => o.value === val)?.label) || val

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: width || 190 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 6, cursor: 'pointer',
          border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)',
        }}
        title={label}
      >
        <span style={{ color: selected.length ? 'var(--text-primary)' : 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selected.length ? `${label} · ${selected.length}` : label}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 50,
          minWidth: '100%', maxHeight: 280, overflowY: 'auto',
          background: 'var(--bg-raised, var(--bg-surface))', border: '1px solid var(--border-strong)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', padding: 4,
        }}>
          {options.length === 0 && (
            <div style={{ padding: 8, fontSize: 12, color: 'var(--text-muted)' }}>No options</div>
          )}
          {options.map(o => (
            <label key={o.value} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 13,
              cursor: 'pointer', borderRadius: 4, color: 'var(--text-primary)', whiteSpace: 'nowrap',
            }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)}
                style={{ cursor: 'pointer', width: 15, height: 15 }} />
              {o.label}
            </label>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {selected.map(val => (
            <span key={val} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
              padding: '2px 6px', borderRadius: 999, background: 'var(--bg-raised, #1e293b)',
              border: '1px solid var(--border)', color: 'var(--text-secondary)',
            }}>
              {labelFor(val)}
              <span onClick={() => toggle(val)} style={{ cursor: 'pointer', fontWeight: 700, lineHeight: 1 }} title="Remove">×</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function FilterBar({
  origins, lendersList,
  hideSelectors = false,
  pendingOrigins, setPendingOrigins,
  pendingLenders, setPendingLenders,
  filtersDirty, applyFilters,
  statusFilter, setStatusFilter,
  searchInput, setSearchInput,
  preset, applyPreset, dateFrom, dateTo, setCustomFrom, setCustomTo,
  includeTests, setIncludeTests,
  total, onRefresh, onExport, exporting,
}) {
  const isMobile = useIsMobile()
  const originOptions = [
    ...((origins?.partners) || []),
    ...((origins?.specials) || []),
  ]
  const lenderOptions = lendersList || []

  if (isMobile) {
  const mSelect = {
    padding: '8px 10px', fontSize: 13, borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--bg-surface)',
    color: 'var(--text-primary)', cursor: 'pointer', width: '100%',
  }
  const mDate = { ...dateInputStyle, flex: 1, minWidth: 0, padding: '8px 10px', fontSize: 13 }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10,
      padding: '14px 16px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
    }}>
      {/* FILTERS + conteo */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }}>
          <Filter size={13} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>FILTERS</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {total != null ? `${total.toLocaleString()} leads` : '—'}
        </span>
      </div>

      {/* Origen / source — multi-select con chips */}
      {!hideSelectors && (
        <MultiChipSelect
          label="All origins"
          options={originOptions}
          selected={pendingOrigins}
          onChange={setPendingOrigins}
          width="100%"
        />
      )}

      {/* Lenders — multi-select con chips */}
      {!hideSelectors && (
        <MultiChipSelect
          label="All lenders"
          options={lenderOptions}
          selected={pendingLenders}
          onChange={setPendingLenders}
          width="100%"
        />
      )}

      {/* Aplicar filtros (origins + lenders). Sin esos dos selectores no
          tiene nada que aplicar, así que en la vista fija se oculta también. */}
      {!hideSelectors && (
        <button onClick={applyFilters} disabled={!filtersDirty} className="btn btn-primary"
          style={{ justifyContent: 'center', opacity: filtersDirty ? 1 : 0.5 }}>
          Apply filters
        </button>
      )}

      {/* Estado */}
      <select value={statusFilter || ''} onChange={(e) => setStatusFilter(e.target.value || null)} style={mSelect}>
        <option value="">All statuses</option>
        <option value="sold">Sold</option>
        <option value="accepted">Accepted</option>
        <option value="rejected">Rejected</option>
      </select>

      {/* Búsqueda por lead ID */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Search size={14} style={{ position: 'absolute', left: 10, color: 'var(--text-muted)' }} />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search lead ID…"
          style={{
            width: '100%', padding: '8px 10px 8px 32px', fontSize: 13, borderRadius: 6,
            border: '1px solid var(--border)', background: 'var(--bg-surface)',
            color: 'var(--text-primary)', fontFamily: 'ui-monospace, monospace',
          }}
        />
      </div>

      {/* Presets: 4 columnas iguales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
        {PRESETS.map(p => (
          <button key={p.id} onClick={() => applyPreset(p.id)} style={{
            padding: '8px 0', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
            background: preset === p.id ? 'var(--bg-raised)' : 'transparent',
            color: preset === p.id ? 'var(--text-primary)' : 'var(--text-muted)',
            border: `1px solid ${preset === p.id ? 'var(--border-strong)' : 'var(--border)'}`,
          }}>{p.label}</button>
        ))}
      </div>

      {/* Rango de fechas */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="date" value={dateFrom || ''} max={dateTo || undefined}
          onChange={(e) => setCustomFrom(e.target.value)} title="From" style={mDate} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>→</span>
        <input type="date" value={dateTo || ''} min={dateFrom || undefined}
          onChange={(e) => setCustomTo(e.target.value)} title="To" style={mDate} />
      </div>

      {/* Include tests */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', userSelect: 'none' }}>
        <input type="checkbox" checked={includeTests} onChange={(e) => setIncludeTests(e.target.checked)} style={{ cursor: 'pointer', width: 16, height: 16 }} />
        Include tests
      </label>

      {/* Acciones */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onExport} disabled={exporting || !total} className="btn btn-ghost"
          style={{ flex: 1, justifyContent: 'center', opacity: (exporting || !total) ? 0.5 : 1 }}
          title={!total ? 'No leads to export' : 'Export leads from the current filter'}>
          {exporting ? <RefreshCw size={14} style={{ animation: 'spin 1.4s linear infinite' }} /> : <Download size={14} />}
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
        <button onClick={onRefresh} className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>
    </div>
  )
}

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      padding: '12px 16px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }}>
        <Filter size={13} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>FILTERS</span>
      </div>

      {/* Origen — multi-select con chips */}
      {!hideSelectors && (
        <MultiChipSelect
          label="All origins"
          options={originOptions}
          selected={pendingOrigins}
          onChange={setPendingOrigins}
          width={200}
        />
      )}

      {/* Lenders — multi-select con chips */}
      {!hideSelectors && (
        <MultiChipSelect
          label="All lenders"
          options={lenderOptions}
          selected={pendingLenders}
          onChange={setPendingLenders}
          width={190}
        />
      )}

      {/* Aplicar filtros (origins + lenders). Sin esos dos selectores no
          tiene nada que aplicar, así que en la vista fija se oculta también. */}
      {!hideSelectors && (
        <button onClick={applyFilters} disabled={!filtersDirty} className="btn btn-primary"
          style={{ opacity: filtersDirty ? 1 : 0.5 }} title="Apply origin & lender filters">
          Apply filters
        </button>
      )}

      {/* Estado */}
      <select
        value={statusFilter || ''}
        onChange={(e) => setStatusFilter(e.target.value || null)}
        style={selectStyle}
      >
        <option value="">All statuses</option>
        <option value="sold">Sold</option>
        <option value="accepted">Accepted</option>
        <option value="rejected">Rejected</option>
      </select>

      {/* Búsqueda por lead ID */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <Search size={13} style={{ position: 'absolute', left: 8, color: 'var(--text-muted)' }} />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search lead ID…"
          style={{
            padding: '6px 10px 6px 26px', fontSize: 12, borderRadius: 6,
            border: '1px solid var(--border)', background: 'var(--bg-surface)',
            color: 'var(--text-primary)', width: 170,
            fontFamily: 'ui-monospace, monospace',
          }}
        />
      </div>

      {/* Atajos de rango */}
      <div style={{ display: 'flex', gap: 5 }}>
        {PRESETS.map(p => (
          <PresetButton
            key={p.id}
            active={preset === p.id}
            label={p.label}
            onClick={() => applyPreset(p.id)}
          />
        ))}
      </div>

      {/* Rango personalizado */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="date"
          value={dateFrom || ''}
          max={dateTo || undefined}
          onChange={(e) => setCustomFrom(e.target.value)}
          style={dateInputStyle}
          title="From"
        />
        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>→</span>
        <input
          type="date"
          value={dateTo || ''}
          min={dateFrom || undefined}
          onChange={(e) => setCustomTo(e.target.value)}
          style={dateInputStyle}
          title="To"
        />
      </div>

      {/* Incluir tests */}
      <label style={{
        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
        fontSize: 11, color: 'var(--text-muted)', userSelect: 'none',
      }}>
        <input
          type="checkbox"
          checked={includeTests}
          onChange={(e) => setIncludeTests(e.target.checked)}
          style={{ cursor: 'pointer' }}
        />
        Include tests
      </label>

      <div style={{ flex: 1 }} />

      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {total != null ? `${total.toLocaleString()} leads` : '—'}
      </span>

      <button
        onClick={onExport}
        disabled={exporting || !total}
        className="btn btn-ghost btn-sm"
        style={{ opacity: (exporting || !total) ? 0.5 : 1 }}
        title={!total ? 'No leads to export' : 'Export leads from the current filter'}
      >
        {exporting
          ? <RefreshCw size={12} style={{ animation: 'spin 1.4s linear infinite' }} />
          : <Download size={12} />}
        {exporting ? 'Exporting…' : 'Export CSV'}
      </button>

      <button onClick={onRefresh} className="btn btn-ghost btn-sm">
        <RefreshCw size={12} />
        Refresh
      </button>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   GRÁFICOS
// ════════════════════════════════════════════════════════════════════════════
function ChartCard({ title, hint, children, style }) {
  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, ...style }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
          color: 'var(--text-muted)', textTransform: 'uppercase',
        }}>
          {title}
        </span>
        {hint && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  )
}

function ChartEmpty({ label, height = 200 }) {
  return (
    <div style={{
      height, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 8, color: 'var(--text-muted)',
    }}>
      <Activity size={22} style={{ opacity: 0.35 }} />
      <span style={{ fontSize: 12 }}>{label}</span>
    </div>
  )
}

// Leads por día — barras apiladas sold/accepted/rejected
function LeadsByDayChart({ series }) {
  if (!series || series.length === 0) {
    return <ChartEmpty label="No data in the selected range" />
  }
  const data = series.map(s => ({ ...s, label: fmtDayLabel(s.date) }))
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
          tickLine={false} axisLine={{ stroke: 'var(--border)' }}
          interval="preserveStartEnd"
        />
        <YAxis
          allowDecimals={false} width={34}
          tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
          tickLine={false} axisLine={false}
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
          cursor={{ fill: 'var(--bg-raised)', opacity: 0.4 }}
        />
        <Bar dataKey="sold" name="Sold" stackId="a" fill="var(--green)" maxBarSize={42} />
        <Bar dataKey="accepted" name="Accepted" stackId="a" fill="var(--amber)" maxBarSize={42} />
        <Bar dataKey="rejected" name="Rejected" stackId="a" fill="var(--red)" maxBarSize={42} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

// Composición sold/accepted/rejected — dona con tasa de aceptación al centro
function CompositionChart({ summary }) {
  const by = summary?.by_status || {}
  const sold = by.sold || 0
  const accepted = by.accepted || 0
  const rejected = by.rejected || 0
  const total = sold + accepted + rejected
  const acceptance = summary?.acceptance_pct ?? 0

  if (total === 0) return <ChartEmpty label="No data in range" />

  const data = [
    { name: 'Sold',     value: sold,     color: 'var(--green)' },
    { name: 'Accepted', value: accepted, color: 'var(--amber)' },
    { name: 'Rejected', value: rejected, color: 'var(--red)' },
  ].filter(d => d.value > 0)

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={data} dataKey="value" nameKey="name"
              cx="50%" cy="50%" innerRadius={52} outerRadius={76}
              paddingAngle={2} strokeWidth={0}
            >
              {data.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <span style={{
            fontFamily: 'ui-monospace, monospace', fontSize: 24, fontWeight: 800,
            color: 'var(--text-primary)',
          }}>
            {acceptance}%
          </span>
          <span style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: 0.5 }}>
            ACCEPTANCE
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 14, marginTop: 8 }}>
        {[
          { name: 'Sold', value: sold, color: 'var(--green)' },
          { name: 'Accepted', value: accepted, color: 'var(--amber)' },
          { name: 'Rejected', value: rejected, color: 'var(--red)' },
        ].map(d => (
          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: d.color }} />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{d.name}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'ui-monospace, monospace' }}>
              {d.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Desglose por origen — barras horizontales rankeadas (partners + Organic + Unidentified + Other)
function OriginBreakdown({ origins }) {
  const entries = (origins || []).filter(o => o && o.count > 0)
  if (entries.length === 0) {
    return <ChartEmpty label="No data in the selected range" height={120} />
  }
  const max = Math.max(...entries.map(e => e.count)) || 1

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {entries.map(({ label, count }) => {
        const muted = label === 'Unidentified' || label === 'Other sources'
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              flex: '0 0 200px', fontSize: 11,
              color: muted ? 'var(--text-muted)' : 'var(--text-secondary)',
              fontStyle: muted ? 'italic' : 'normal',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }} title={label}>
              {label}
            </span>
            <div style={{ flex: 1, height: 8, background: 'var(--bg-raised)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                width: `${(count / max) * 100}%`, height: '100%',
                background: 'var(--text-secondary)', opacity: muted ? 0.35 : 0.6, borderRadius: 4,
              }} />
            </div>
            <span style={{
              flex: '0 0 56px', textAlign: 'right', fontSize: 11, fontWeight: 700,
              color: 'var(--text-primary)', fontFamily: 'ui-monospace, monospace',
            }}>
              {count.toLocaleString()}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// Motivos de rechazo — barras horizontales rankeadas
function RejectionReasons({ reasons }) {
  const entries = Object.entries(reasons || {}).sort((a, b) => b[1] - a[1])
  if (entries.length === 0) {
    return <ChartEmpty label="No rejections in the selected range" height={120} />
  }
  const max = Math.max(...entries.map(e => e[1])) || 1

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {entries.map(([reason, count]) => (
        <div key={reason} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            flex: '0 0 200px', fontSize: 11, color: 'var(--text-secondary)',
            fontFamily: 'ui-monospace, monospace',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }} title={reason}>
            {reason}
          </span>
          <div style={{ flex: 1, height: 8, background: 'var(--bg-raised)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: `${(count / max) * 100}%`, height: '100%',
              background: 'var(--red)', opacity: 0.7, borderRadius: 4,
            }} />
          </div>
          <span style={{
            flex: '0 0 44px', textAlign: 'right', fontSize: 11, fontWeight: 700,
            color: 'var(--text-primary)', fontFamily: 'ui-monospace, monospace',
          }}>
            {count.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   WINNING BID  (Tarea 4 — average winning bid + análisis de ventana)
//
//   Son DOS productos distintos y por eso van en contenedores separados:
//   la tira de arriba es un indicador EN VIVO; el análisis de ventana es un
//   estudio que se mira una vez y se cierra (por eso es colapsable).
// ════════════════════════════════════════════════════════════════════════════

// Cifras de dinero: 2 decimales. Antes usábamos 4 (para separar Highwinds
// $0.5451 de otros), pero eso metía ruido visual en el número que debe gritar
// la respuesta. $0.55 se lee perfecto y la decisión de Mario se toma en centavos.
const fmtBid = (v) =>
  (v === null || v === undefined || Number.isNaN(Number(v)))
    ? '—'
    : `$${Number(v).toFixed(2)}`

// Delta de dinero con signo explícito: +$0.14 / -$0.03. El signo es el mensaje.
const fmtBidDelta = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  const sign = n > 0 ? '+' : n < 0 ? '−' : ''
  return `${sign}$${Math.abs(n).toFixed(2)}`
}

// Porcentajes enteros: 89%, no 89.3%.
const fmtPct = (v) =>
  (v === null || v === undefined || Number.isNaN(Number(v)))
    ? '—'
    : `${Math.round(Number(v))}%`

const fmtSeconds = (ms) =>
  (ms === null || ms === undefined) ? '—' : `${Math.round(Number(ms) / 1000)}s`

// ── A. Tira de KPIs ─────────────────────────────────────────────────────────
//   La mediana al lado del promedio hace obvio el sesgo de la distribución sin
//   tener que explicarlo: si el promedio es $1.49 y la mediana $0.55, unos
//   pocos compradores caros están cargando todo el promedio.
function WinningBidStrip({ data, loading }) {
  const isMobile = useIsMobile()
  const o = data?.overall || {}
  const busy = loading || !data

  const currentSec = data?.current_window_ms
    ? Math.round(data.current_window_ms / 1000)
    : null

  const cells = [
    {
      key: 'avg', label: `Average winning bid${currentSec ? ` · at ${currentSec}s today` : ''}`, big: true,
      val: busy ? '—' : fmtBid(o.average_winning_bid), color: 'var(--green)',
    },
    {
      key: 'median', label: 'Median bid',
      val: busy ? '—' : fmtBid(o.median_bid), color: 'var(--text-primary)',
    },
    {
      key: 'sold', label: 'Sold leads',
      val: busy ? '—' : Number(o.sold_count || 0).toLocaleString(), color: 'var(--text-primary)',
    },
    {
      key: 'range', label: 'Range (min – max)',
      val: busy ? '—' : `${fmtBid(o.min_bid)} – ${fmtBid(o.max_bid)}`, color: 'var(--text-secondary)',
    },
  ]

  const note = 'winning bids from the auction log · redirects returned, not confirmed landings'

  if (isMobile) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 12,
        padding: '16px 18px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <span style={{
          fontSize: 10, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700,
        }}>Winning bid</span>

        {cells.map(c => (
          <div key={c.key} style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: c.big ? 23 : 18, fontWeight: 800,
              color: c.color, opacity: busy ? 0.4 : 1,
            }}>{c.val}</span>
            <span style={{
              fontSize: 10, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600,
            }}>{c.label}</span>
          </div>
        ))}

        <span style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.5 }}>{note}</span>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex', gap: 28, alignItems: 'center',
      padding: '16px 24px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      flexWrap: 'wrap',
    }}>
      <span style={{
        fontSize: 10, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700,
      }}>
        Winning bid
      </span>
      <span style={{ color: 'var(--border-strong)', opacity: 0.4 }}>|</span>
      {cells.map((c, i) => (
        <React.Fragment key={c.key}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: c.big ? 26 : 19, fontWeight: 800, color: c.color,
              opacity: busy ? 0.4 : 1,
            }}>
              {c.val}
            </span>
            <span style={{
              fontSize: 10, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600,
            }}>
              {c.label}
            </span>
          </div>
          {i < cells.length - 1 && (
            <span style={{ color: 'var(--border-strong)', opacity: 0.4 }}>|</span>
          )}
        </React.Fragment>
      ))}
      <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-muted)' }}>
        {note}
      </span>
    </div>
  )
}

// ── B. Volumen vs revenue por comprador ─────────────────────────────────────
//   DOS barras por comprador. Ese contraste ES el hallazgo: highwinds pone el
//   82% del volumen y una fracción del dinero; EPC es lo contrario.
function BuyerSplitBreakdown({ buyers }) {
  const entries = (buyers || []).filter(b => b && b.sold_count > 0)
  if (entries.length === 0) {
    return <ChartEmpty label="No sold leads in the selected range" height={120} />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        {[
          { name: 'Volume', color: 'var(--text-secondary)' },
          { name: 'Revenue', color: 'var(--green)' },
        ].map(l => (
          <div key={l.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: l.color }} />
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{l.name}</span>
          </div>
        ))}
      </div>

      {entries.map(b => (
        <div key={b.buyer} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: 'var(--text-primary)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }} title={b.buyer}>
              {b.buyer}
            </span>
            <span style={{
              fontSize: 10, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace',
            }}>
              {Number(b.sold_count).toLocaleString()} · avg {fmtBid(b.average_winning_bid)}
              {b.median_elapsed_ms !== null && b.median_elapsed_ms !== undefined
                ? ` · p50 ${fmtSeconds(b.median_elapsed_ms)}`
                : ''}
            </span>
          </div>

          {[
            { key: 'vol', label: 'vol', pct: b.share_of_volume_pct, color: 'var(--text-secondary)', opacity: 0.55 },
            { key: 'rev', label: 'rev', pct: b.share_of_revenue_pct, color: 'var(--green)', opacity: 0.85 },
          ].map(bar => (
            <div key={bar.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                flex: '0 0 26px', fontSize: 9, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: 0.4,
              }}>
                {bar.label}
              </span>
              <div style={{
                flex: 1, height: 8, background: 'var(--bg-raised)',
                borderRadius: 4, overflow: 'hidden',
              }}>
                <div style={{
                  width: `${Math.max(0, Math.min(100, Number(bar.pct) || 0))}%`,
                  height: '100%', background: bar.color, opacity: bar.opacity, borderRadius: 4,
                }} />
              </div>
              <span style={{
                flex: '0 0 44px', textAlign: 'right', fontSize: 11, fontWeight: 700,
                color: 'var(--text-primary)', fontFamily: 'ui-monospace, monospace',
              }}>
                {fmtPct(bar.pct)}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

// ── C. Promedio diario ──────────────────────────────────────────────────────
function WinningBidByDayChart({ byDay }) {
  const rows = (byDay || []).filter(d => d && d.sold_count > 0)
  if (rows.length === 0) {
    return <ChartEmpty label="No data in the selected range" />
  }
  const data = rows.map(d => ({
    label: fmtDayLabel(d.date),
    avg: Number(d.average_winning_bid) || 0,
    sold: d.sold_count,
  }))

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
          tickLine={false} axisLine={{ stroke: 'var(--border)' }}
          interval="preserveStartEnd"
        />
        <YAxis
          width={46}
          tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
          tickLine={false} axisLine={false}
          tickFormatter={(v) => `$${Number(v).toFixed(2)}`}
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
          formatter={(v, name) => (name === 'Avg winning bid' ? fmtBid(v) : v)}
          cursor={{ stroke: 'var(--border-strong)', strokeWidth: 1 }}
        />
        <Line
          type="monotone" dataKey="avg" name="Avg winning bid"
          stroke="var(--green)" strokeWidth={2}
          dot={false} activeDot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

// ── D. Análisis de ventana de subasta ───────────────────────────────────────
//   Este bloque responde LA pregunta de la Tarea 4: ¿cuánto sube el avg bid si
//   le doy más segundos a la subasta, y cuánto tiempo cuesta ese aumento?
//   Cada fila muestra, lado a lado: la ventana, los segundos EXTRA sobre la
//   fila anterior (el costo), el avg bid a esa ventana, y DOS comparaciones del
//   avg bid: contra la fila anterior (salto marginal) y contra los 110s de hoy.
//   Abierto por defecto: es el centro de la tarea, no un detalle escondido.
function AuctionWindowAnalysis({ data, loading }) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(true)

  const currentSec = data?.current_window_ms
    ? Math.round(data.current_window_ms / 1000)
    : null

  const rows = useMemo(() => {
    const sim = data?.window_simulation || []
    // avg bid de la ventana más larga (la de hoy) = referencia "contra 110s".
    const fullAvg = sim.length ? (sim[sim.length - 1].average_winning_bid || 0) : 0
    return sim.map((r, i) => {
      const prev = i > 0 ? sim[i - 1] : null
      // +segundos: cuánto tiempo extra sobre la fila anterior.
      const addedSec = prev ? (r.window_seconds - prev.window_seconds) : null
      // +$/lead vs fila anterior: cuánto sube el avg bid en este tramo.
      const dBidPrev = prev
        ? (r.average_winning_bid || 0) - (prev.average_winning_bid || 0)
        : null
      // vs 110s: cuánto le falta a esta ventana para el avg bid de hoy.
      const dBidFull = (r.average_winning_bid || 0) - fullAvg
      // $/lead POR SEGUNDO en este tramo: el dato que hace la decisión obvia.
      const perSec = (addedSec && addedSec > 0 && dBidPrev !== null)
        ? dBidPrev / addedSec
        : null
      return { ...r, addedSec, dBidPrev, dBidFull, perSec }
    })
  }, [data])

  // Titular en el lenguaje de Mario: dinero por segundo, no porcentajes.
  //   Busca el mejor tramo (mayor $/lead ganado) y el peor tramo tardío.
  const headline = useMemo(() => {
    if (loading || !data || rows.length < 2) return 'Loading auction window analysis…'
    const withPrev = rows.filter(r => r.dBidPrev !== null && r.addedSec)
    if (!withPrev.length) return `Current window: ${currentSec ? `${currentSec}s` : 'unknown'}`
    // El último tramo (llegar hasta 110s) es el que Mario cuestiona.
    const last = withPrev[withPrev.length - 1]
    const lastPerSec = last.perSec || 0
    return `Current window: ${currentSec ? `${currentSec}s` : 'unknown'}` +
      ` · the last ${last.addedSec}s (to ${last.window_seconds}s) add ` +
      `${fmtBidDelta(last.dBidPrev)} per lead` +
      ` — about ${fmtBidDelta(lastPerSec)} per extra second`
  }, [rows, data, loading, currentSec])

  // Grid: WINDOW · +SEC · AVG BID · +$/LEAD (vs prev) · VS 110s · SOLD
  const COLS = '78px 70px 84px 96px 88px 128px'
  const headers = [
    { t: 'WINDOW',    align: 'left'  },
    { t: '+SEC',      align: 'right' },
    { t: 'AVG BID',   align: 'right' },
    { t: '+$/LEAD',   align: 'right' },
    { t: 'VS 110s',   align: 'right' },
    { t: 'SOLD',      align: 'right' },
  ]

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: isMobile ? '14px 16px' : '14px 24px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          textAlign: 'left', color: 'inherit',
        }}
      >
        <span style={{
          fontSize: 10, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700,
          whiteSpace: 'nowrap',
        }}>
          Auction window
        </span>
        <span style={{
          flex: 1, fontSize: isMobile ? 11 : 12, lineHeight: 1.5,
          color: 'var(--text-secondary)',
        }}>
          {headline}
        </span>
        <ChevronDown
          size={16}
          style={{
            flex: '0 0 auto', color: 'var(--text-muted)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease',
          }}
        />
      </button>

      {open && (
        <div style={{
          padding: isMobile ? '0 16px 16px' : '0 24px 18px',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          {/* Cómo leer la tabla, en una línea, para que nadie la malinterprete. */}
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Each row is a candidate time window. <strong style={{ color: 'var(--text-primary)' }}>+SEC</strong> is
            the extra wait over the row above; <strong style={{ color: 'var(--text-primary)' }}>+$/LEAD</strong> is
            how much the average winning bid rises for that wait. Read them together:
            is the money worth the seconds?
          </span>

          {rows.length === 0 ? (
            <ChartEmpty label="No auction data in the selected range" height={120} />
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map(r => {
                const censored = r.censored_leads > 0
                return (
                  <div key={r.window_seconds} style={{
                    padding: '10px 12px',
                    background: 'var(--bg-raised)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    opacity: censored ? 0.5 : 1,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{
                        fontSize: 14, fontWeight: 800, color: 'var(--text-primary)',
                        fontFamily: 'ui-monospace, monospace',
                      }}>
                        {r.window_seconds}s
                        {r.addedSec ? (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>
                            {' '}(+{r.addedSec}s)
                          </span>
                        ) : null}
                      </span>
                      <span style={{
                        fontSize: 15, fontWeight: 800, fontFamily: 'ui-monospace, monospace',
                        color: 'var(--green)',
                      }}>
                        {fmtBid(r.average_winning_bid)}
                      </span>
                    </div>
                    <div style={{
                      marginTop: 6, display: 'flex', justifyContent: 'space-between',
                      fontSize: 11, fontFamily: 'ui-monospace, monospace',
                    }}>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {Number(r.sold_count).toLocaleString()} sold · {fmtPct(r.sold_pct)}
                      </span>
                      <span style={{
                        color: r.dBidPrev && r.dBidPrev > 0 ? 'var(--amber)' : 'var(--text-muted)',
                        fontWeight: 700,
                      }}>
                        {r.dBidPrev !== null ? `${fmtBidDelta(r.dBidPrev)}/lead` : 'baseline'}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 544 }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: COLS, gap: 8,
                  padding: '0 4px 8px',
                  borderBottom: '1px solid var(--border)',
                }}>
                  {headers.map(h => (
                    <span key={h.t} style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: 0.6,
                      color: 'var(--text-muted)', textAlign: h.align,
                    }}>
                      {h.t}
                    </span>
                  ))}
                </div>

                {rows.map(r => {
                  const censored = r.censored_leads > 0
                  const isCurrent = currentSec && r.window_seconds === currentSec
                  return (
                    <div
                      key={r.window_seconds}
                      title={censored
                        ? `${r.censored_leads.toLocaleString()} auctions ran with a shorter timeout — this row understates the real bid`
                        : undefined}
                      style={{
                        display: 'grid', gridTemplateColumns: COLS, gap: 8,
                        padding: '9px 4px',
                        borderBottom: '1px solid var(--border)',
                        alignItems: 'center',
                        opacity: censored ? 0.45 : 1,
                        background: isCurrent ? 'var(--bg-raised)' : 'transparent',
                      }}
                    >
                      {/* WINDOW */}
                      <span style={{
                        fontSize: 12, fontFamily: 'ui-monospace, monospace', fontWeight: 800,
                        color: 'var(--text-primary)', textAlign: 'left',
                      }}>
                        {r.window_seconds}s{isCurrent ? ' •' : ''}
                      </span>
                      {/* +SEC */}
                      <span style={{
                        fontSize: 11, fontFamily: 'ui-monospace, monospace', fontWeight: 600,
                        color: 'var(--text-muted)', textAlign: 'right',
                      }}>
                        {r.addedSec ? `+${r.addedSec}s` : '—'}
                      </span>
                      {/* AVG BID */}
                      <span style={{
                        fontSize: 12, fontFamily: 'ui-monospace, monospace', fontWeight: 800,
                        color: 'var(--green)', textAlign: 'right',
                      }}>
                        {fmtBid(r.average_winning_bid)}
                      </span>
                      {/* +$/LEAD vs prev */}
                      <span style={{
                        fontSize: 11, fontFamily: 'ui-monospace, monospace', fontWeight: 800,
                        color: r.dBidPrev && r.dBidPrev > 0 ? 'var(--amber)' : 'var(--text-muted)',
                        textAlign: 'right',
                      }}>
                        {r.dBidPrev !== null ? fmtBidDelta(r.dBidPrev) : '—'}
                      </span>
                      {/* VS 110s */}
                      <span style={{
                        fontSize: 11, fontFamily: 'ui-monospace, monospace', fontWeight: 600,
                        color: 'var(--text-secondary)', textAlign: 'right',
                      }}>
                        {Math.abs(r.dBidFull) < 0.005 ? '—' : fmtBidDelta(r.dBidFull)}
                      </span>
                      {/* SOLD */}
                      <span style={{
                        fontSize: 11, fontFamily: 'ui-monospace, monospace', fontWeight: 600,
                        color: 'var(--text-secondary)', textAlign: 'right',
                      }}>
                        {Number(r.sold_count).toLocaleString()} · {fmtPct(r.sold_pct)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Advertencia: la simulación mide lo que se PIERDE al recortar, no
              lo que se GANA por menos abandono. Sin esto el número engaña. */}
          <div style={{
            display: 'flex', gap: 10, alignItems: 'flex-start',
            padding: '10px 12px',
            background: 'var(--amber-bg, var(--bg-raised))',
            border: '1px solid var(--amber)',
            borderRadius: 8,
          }}>
            <AlertTriangle size={14} style={{ flex: '0 0 auto', color: 'var(--amber)', marginTop: 1 }} />
            <span style={{ fontSize: 11, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
              This measures only the bid gained by waiting longer — not the sales lost
              to abandonment while the user waits. A longer window also loses leads that
              never finish the auction, and that side is not measured here yet.
            </span>
          </div>

          <span style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Simulated over the leads in the current filter, replaying each bid&apos;s own
            elapsed time. <strong>+$/LEAD</strong> compares the average winning bid to the
            row above; <strong>VS 110s</strong> compares it to today&apos;s full window.
            The <strong>•</strong> row is the window running in production now. Dimmed rows
            are incomplete — those auctions ran with a shorter timeout, so a longer window
            can&apos;t be measured for them.
          </span>
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   PAGINACIÓN
// ════════════════════════════════════════════════════════════════════════════
function Pagination({ page, totalPages, total, limit, onPageChange }) {
  if (totalPages <= 1) return null

  const start = (page - 1) * limit + 1
  const end = Math.min(page * limit, total)

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 16px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        Showing <strong style={{ color: 'var(--text-primary)' }}>{start}–{end}</strong> of{' '}
        <strong style={{ color: 'var(--text-primary)' }}>{total.toLocaleString()}</strong> leads
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="btn btn-ghost btn-sm"
          aria-label="Previous page"
          style={{ padding: '4px 8px', opacity: page === 1 ? 0.4 : 1 }}
        >
          <ChevronLeft size={14} />
        </button>
        <span style={{
          fontFamily: 'ui-monospace, monospace', fontSize: 12,
          color: 'var(--text-secondary)', minWidth: 70, textAlign: 'center',
        }}>
          {page} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="btn btn-ghost btn-sm"
          aria-label="Next page"
          style={{ padding: '4px 8px', opacity: page === totalPages ? 0.4 : 1 }}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   TABLA  (con columna ORIGEN)
// ════════════════════════════════════════════════════════════════════════════
const TABLE_GRID = '210px 132px 100px 120px 1fr 116px 110px 104px 116px'
const TABLE_MIN_WIDTH = 1280

// Tarjeta de lead para móvil (espeja las columnas de la tabla desktop)
function LeadCardAdmin({ lead: l }) {
  const hasValue   = l.front_end_value !== null && l.front_end_value !== undefined
  const hasOur     = l.our_value !== null && l.our_value !== undefined
  const hasPartner = l.partner_value !== null && l.partner_value !== undefined && l.partner_value > 0

  const moneyCell = (label, value, on, color) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={{
        fontSize: 9, letterSpacing: 0.6, fontWeight: 700,
        color: 'var(--text-muted)', textTransform: 'uppercase',
      }}>{label}</span>
      <span style={{
        fontFamily: 'ui-monospace, monospace', fontSize: 13, fontWeight: 700,
        color: on ? color : 'var(--text-muted)',
      }}>{fmtMoney(value)}</span>
    </div>
  )

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10,
      padding: '14px 16px', borderBottom: '1px solid var(--border)',
    }}>
      {/* Lead ID + estado */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
        <span style={{
          fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600,
          color: 'var(--text-primary)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }} title={l.kickoff_lead_id}>{l.kickoff_lead_id || '—'}</span>
        <StatusBadge status={l.partner_status} />
      </div>

      {/* Origen + fecha */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <OriginBadge origin={l.origin || l.partner} />
        <span style={{ color: 'var(--border-strong)', opacity: 0.4, fontSize: 11 }}>·</span>
        <span style={{ color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>
          {fmtDateTime(l.createdAt)}
        </span>
      </div>

      {/* Comprador (solo si fue vendido) */}
      {l.winner_lender && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 9, letterSpacing: 0.6, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Buyer</span>
          <BuyerBadge lender={l.winner_lender} />
        </div>
      )}

      {/* Motivo de rechazo (solo si aplica) */}
      {l.rejection_reason && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
            padding: '2px 6px', borderRadius: 4,
            background: 'var(--red-bg)', color: 'var(--red)',
            fontFamily: 'ui-monospace, monospace',
          }}>{l.rejection_reason}</span>
          {l.rejection_detail && (
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }} title={l.rejection_detail}>
              {l.rejection_detail}
            </span>
          )}
        </div>
      )}

      {/* Dinero: postback / our share / partner share */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
        paddingTop: 10, borderTop: '1px solid var(--border)',
      }}>
        {moneyCell('Total', l.front_end_value, hasValue, 'var(--text-primary)')}
        {moneyCell('Our share', l.our_value, hasOur, 'var(--green)')}
        {moneyCell('Partner share', l.partner_value, hasPartner, 'var(--amber)')}
      </div>
    </div>
  )
}


function LeadsTable({ leads }) {

  const isMobile = useIsMobile()   
  
  if (leads.length === 0) {
    return (
      <div className="card" style={{
        padding: 60, textAlign: 'center',
        color: 'var(--text-muted)',
      }}>
        <Activity size={28} style={{ opacity: 0.4 }} />
        <p style={{ marginTop: 12, fontSize: 13 }}>
          No leads match this filter.
        </p>
      </div>
    )
  }

   if (isMobile) {
    return (
      <div className="card" style={{ padding: 0 }}>
        {leads.map((l) => <LeadCardAdmin key={l.kickoff_lead_id} lead={l} />)}
      </div>
    )
  }

  return (
    <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: TABLE_GRID,
        minWidth: TABLE_MIN_WIDTH,
        gap: 12,
        padding: '10px 16px',
        borderBottom: '1px solid var(--border)',
        fontSize: 9, color: 'var(--text-muted)',
        letterSpacing: 1.2, fontWeight: 700,
        background: 'var(--bg-raised)',
        fontFamily: 'ui-monospace, monospace',
      }}>
        <span>LEAD ID</span>
        <span>ORIGIN</span>
        <span>STATUS</span>
        <span>BUYER</span>
        <span>REASON (if rejected)</span>
        <span>RECEIVED AT</span>
        <span>TOTAL</span>
        <span>OUR SHARE</span>
        <span>PARTNER SHARE</span>
      </div>

      {leads.map((l) => {
        const hasValue = l.front_end_value !== null && l.front_end_value !== undefined
        const hasOur = l.our_value !== null && l.our_value !== undefined
        const hasPartner = l.partner_value !== null && l.partner_value !== undefined && l.partner_value > 0
        return (
        <div key={l.kickoff_lead_id} style={{
          display: 'grid',
          gridTemplateColumns: TABLE_GRID,
          minWidth: TABLE_MIN_WIDTH,
          gap: 12,
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          fontSize: 11, alignItems: 'center',
        }}>
          <span style={{
            fontFamily: 'ui-monospace, monospace',
            fontWeight: 600, color: 'var(--text-primary)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }} title={l.kickoff_lead_id}>
            {l.kickoff_lead_id || '—'}
          </span>

          <OriginBadge origin={l.origin || l.partner} />

          <StatusBadge status={l.partner_status} />

          <BuyerBadge lender={l.winner_lender} />

          <div style={{ minWidth: 0 }}>
            {l.rejection_reason ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                  padding: '2px 6px', borderRadius: 4,
                  background: 'var(--red-bg)', color: 'var(--red)',
                  border: '1px solid var(--red)30',
                  fontFamily: 'ui-monospace, monospace',
                }}>
                  {l.rejection_reason}
                </span>
                {l.rejection_detail && (
                  <span style={{
                    fontSize: 10, color: 'var(--text-muted)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }} title={l.rejection_detail}>
                    {l.rejection_detail}
                  </span>
                )}
              </div>
            ) : (
              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
            )}
          </div>

          {/* Received At */}
          <span style={{
            color: 'var(--text-secondary)',
            fontFamily: 'ui-monospace, monospace', fontSize: 10,
          }} title={fmtDateTime(l.createdAt)}>
            {fmtDateTime(l.createdAt)}
          </span>

          {/* Postback total (valor completo del postback) */}
          <span style={{
            fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 700,
            color: hasValue ? 'var(--text-primary)' : 'var(--text-muted)',
          }}>
            {fmtMoney(l.front_end_value)}
          </span>

          {/* Our share (lo que nos queda) */}
          <span style={{
            fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 700,
            color: hasOur ? 'var(--green)' : 'var(--text-muted)',
          }}>
            {fmtMoney(l.our_value)}
          </span>

          {/* Partner share (lo que le toca al partner) */}
          <span style={{
            fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 700,
            color: hasPartner ? 'var(--amber)' : 'var(--text-muted)',
          }}>
            {fmtMoney(l.partner_value)}
          </span>
        </div>
        )
      })}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   PÁGINA PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════
export default function AdminLeadsDashboardPage({
  // Props opcionales para reusar esta página como vista fija de un origen
  // (p.ej. PartnerLeadsPage, que fija lockedOrigin al aff_id de un partner).
  // Por defecto (sin props) = Leads Dashboard general, todos los orígenes.
  lockedOrigin = null,        // aff_id fijo; oculta el filtro de orígenes
  pageTitle = 'Leads Dashboard',
  pageSubtitle = null,
} = {}) {
  const [origins, setOrigins] = useState({ partners: [], specials: [] })
  const [lendersList, setLendersList] = useState([])
  const [summary, setSummary] = useState(null)
  const [winningBid, setWinningBid] = useState(null)
  const [winningBidLoading, setWinningBidLoading] = useState(true)
  const [leadsResp, setLeadsResp] = useState({
    leads: [], total: 0, page: 1, total_pages: 1, limit: PAGE_SIZE,
  })
  // Filtros aplicados (disparan el fetch) vs. selección pendiente (chips en edición).
  const [selectedOrigins, setSelectedOrigins] = useState([])   // aplicados
  const [selectedLenders, setSelectedLenders] = useState([])   // aplicados
  const [pendingOrigins, setPendingOrigins] = useState([])     // en edición
  const [pendingLenders, setPendingLenders] = useState([])     // en edición
  const [statusFilter, setStatusFilter] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')                 // búsqueda con debounce
  const [includeTests, setIncludeTests] = useState(false)
  const [dateFrom, setDateFrom] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [dateTo, setDateTo] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [preset, setPreset] = useState('today')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)

  // ¿Hay cambios sin aplicar en los multi-filtros? (para resaltar el botón Apply)
  const filtersDirty = useMemo(() => {
    const a = [...pendingOrigins].sort().join(',') !== [...selectedOrigins].sort().join(',')
    const b = [...pendingLenders].sort().join(',') !== [...selectedLenders].sort().join(',')
    return a || b
  }, [pendingOrigins, selectedOrigins, pendingLenders, selectedLenders])

  const applyFilters = useCallback(() => {
    setSelectedOrigins(pendingOrigins)
    setSelectedLenders(pendingLenders)
    setPage(1)
  }, [pendingOrigins, pendingLenders])

  // Debounce de la búsqueda (400ms) → evita un request por tecla
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  // Params comunes a summary y leads
  const commonParams = useCallback(() => {
    const p = {}
    if (dateFrom) p.date_from = dateFrom
    if (dateTo) p.date_to = dateTo
    // Vista fija: el origen queda clavado y no se puede cambiar.
    // Vista general: usa la selección del usuario.
    if (lockedOrigin) {
      p.origins = lockedOrigin
    } else if (selectedOrigins.length) {
      p.origins = selectedOrigins.join(',')
    }
    if (selectedLenders.length) p.lenders = selectedLenders.join(',')
    if (includeTests) p.include_tests = true
    return p
  }, [dateFrom, dateTo, selectedOrigins, selectedLenders, includeTests, lockedOrigin])

  const applyPreset = useCallback((p) => {
    const today = new Date()
    const todayStr = format(today, 'yyyy-MM-dd')
    if (p === 'today') {
      setDateFrom(todayStr); setDateTo(todayStr)
    } else if (p === '7d') {
      setDateFrom(format(subDays(today, 6), 'yyyy-MM-dd')); setDateTo(todayStr)
    } else if (p === '30d') {
      setDateFrom(format(subDays(today, 29), 'yyyy-MM-dd')); setDateTo(todayStr)
    } else if (p === 'ytd') {
      const ytdStart = `${today.getFullYear()}-01-01`
      setDateFrom(ytdStart); setDateTo(todayStr)
    } else {
      setDateFrom(null); setDateTo(null)
    }
    setPreset(p)
  }, [])


  const setCustomFrom = useCallback((v) => { setDateFrom(v || null); setPreset('custom') }, [])
  const setCustomTo   = useCallback((v) => { setDateTo(v || null);   setPreset('custom') }, [])

  const fetchOrigins = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/leads/origins')
      setOrigins({ partners: data.partners || [], specials: data.specials || [] })
    } catch (err) {
      console.error('[admin] fetchOrigins failed:', err)
      if (err.response?.status === 403) {
        setError('This section is only available to admin users.')
      }
    }
  }, [])

  const fetchLenders = useCallback(async () => {
    // En la vista fija el selector de lenders no se muestra: nos ahorramos
    // la petición.
    if (lockedOrigin) return
    try {
      const { data } = await api.get('/admin/leads/lenders')
      setLendersList(data.lenders || [])
    } catch (err) {
      console.error('[admin] fetchLenders failed:', err)
    }
  }, [lockedOrigin])

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/leads/summary', { params: commonParams() })
      setSummary(data)
    } catch (err) {
      console.error('[admin] fetchSummary failed:', err)
    }
  }, [commonParams])

  const fetchWinningBid = useCallback(async () => {
    // En la vista fija de un solo origen ninguna sección consume winningBid,
    // así que evitamos la agregación pesada sobre auctionLogs por completo.
    if (lockedOrigin) { setWinningBidLoading(false); return }
    try {
      const { data } = await api.get('/admin/leads/winning-bid', { params: commonParams() })
      setWinningBid(data)
    } catch (err) {
      console.error('[admin] fetchWinningBid failed:', err)
    } finally {
      setWinningBidLoading(false)
    }
  }, [commonParams, lockedOrigin])

  const fetchLeads = useCallback(async () => {
    try {
      const params = { page, limit: PAGE_SIZE, ...commonParams() }
      if (statusFilter) params.status = statusFilter
      if (q) params.q = q
      const { data } = await api.get('/admin/leads', { params })
      setLeadsResp({
        leads: data.leads || [],
        total: data.total ?? 0,
        page: data.page || 1,
        total_pages: data.total_pages || 1,
        limit: data.limit || PAGE_SIZE,
      })
      setError(null)
    } catch (err) {
      console.error('[admin] fetchLeads failed:', err)
      if (err.response?.status === 403) {
        setError('This section is only available to admin users.')
      } else {
        toast.error('Error loading leads')
      }
    } finally {
      setLoading(false)
    }
  }, [page, statusFilter, q, commonParams])

  useEffect(() => { fetchOrigins() }, [fetchOrigins])
  useEffect(() => { fetchLenders() }, [fetchLenders])

  useEffect(() => {
    fetchLeads()
    fetchSummary()
  }, [fetchLeads, fetchSummary])

  // Polling (respeta los filtros activos)
  useEffect(() => {
    const t = setInterval(() => {
      fetchSummary()
      fetchLeads()
    }, POLL_MS)
    return () => clearInterval(t)
  }, [fetchSummary, fetchLeads])

  // Winning bid: refresco inmediato al cambiar filtros + ciclo propio de 60s.
  useEffect(() => { fetchWinningBid() }, [fetchWinningBid])
  useEffect(() => {
    const t = setInterval(() => { fetchWinningBid() }, WINNING_BID_POLL_MS)
    return () => clearInterval(t)
  }, [fetchWinningBid])

  // Reset a página 1 al cambiar cualquier filtro
  useEffect(() => {
    setPage(1)
  }, [statusFilter, selectedOrigins, selectedLenders, q, includeTests, dateFrom, dateTo])

  const handleManualRefresh = () => {
    setLoading(true)
    fetchSummary()
    fetchLeads()
    fetchWinningBid()
    toast.success('Data refreshed')
  }

  // Exporta TODOS los leads del filtro activo (no solo la página visible)
  const handleExport = async () => {
    setExporting(true)
    try {
      const baseParams = { limit: EXPORT_PAGE_LIMIT, ...commonParams() }
      if (statusFilter) baseParams.status = statusFilter
      if (q) baseParams.q = q

      let all = []
      let p = 1
      let pages = 1
      do {
        const { data } = await api.get('/admin/leads', { params: { ...baseParams, page: p } })
        all = all.concat(data.leads || [])
        pages = data.total_pages || 1
        p += 1
      } while (p <= pages && p <= EXPORT_MAX_PAGES)

      if (all.length === 0) {
        toast.error('No leads to export')
        return
      }

      const stamp = format(new Date(), 'yyyyMMdd_HHmm')
      const who = selectedOrigins.length ? selectedOrigins.join('-') : 'all'
      downloadCsv(buildCsv(all), `leads_admin_${who}_${stamp}.csv`)

      const capped = pages > EXPORT_MAX_PAGES
      toast.success(
        `${all.length.toLocaleString()} leads exported` +
        (capped ? ` (limit ${EXPORT_MAX_PAGES * EXPORT_PAGE_LIMIT})` : '')
      )
    } catch (err) {
      console.error('[admin] export failed:', err)
      toast.error('Error exporting CSV')
    } finally {
      setExporting(false)
    }
  }

  // Bloqueo total si no es admin (el backend devolvió 403)
  if (error && !summary) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <XCircle size={36} style={{ color: 'var(--red)', opacity: 0.6 }} />
        <h2 style={{ marginTop: 16, fontSize: 18, color: 'var(--text-primary)' }}>
          Access denied
        </h2>
        <p style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
          {error}
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Same range the summary, the charts and the table below are filtered
          to, so a settlement that explains the figures is read alongside
          them rather than found later. */}
      <SettlementNoticeBanner dateFrom={dateFrom} dateTo={dateTo} />

      <div style={{ fontSize: 12, color: 'var(--text-muted)', paddingLeft: 4 }}>
        {lockedOrigin ? (
          <>
            <strong style={{ color: 'var(--text-secondary)' }}>{pageTitle}</strong>
            {pageSubtitle ? <>{' · '}{pageSubtitle}</> : null}
            {' · '}updates every {POLL_MS / 1000}s
          </>
        ) : (
          <>
            <strong style={{ color: 'var(--text-secondary)' }}>All leads</strong>
            {' · '}
            every origin (partners, organic &amp; unidentified) · updates every {POLL_MS / 1000}s
          </>
        )}
      </div>

      <FilterBar
        origins={origins}
        lendersList={lendersList}
        hideSelectors={!!lockedOrigin}
        pendingOrigins={pendingOrigins}
        setPendingOrigins={setPendingOrigins}
        pendingLenders={pendingLenders}
        setPendingLenders={setPendingLenders}
        filtersDirty={filtersDirty}
        applyFilters={applyFilters}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        preset={preset}
        applyPreset={applyPreset}
        dateFrom={dateFrom}
        dateTo={dateTo}
        setCustomFrom={setCustomFrom}
        setCustomTo={setCustomTo}
        includeTests={includeTests}
        setIncludeTests={setIncludeTests}
        total={leadsResp.total}
        onRefresh={handleManualRefresh}
        onExport={handleExport}
        exporting={exporting}
      />

      <StatsStrip summary={summary} />

      <RevenueStrip summary={summary} />

      {/* En la vista fija de un solo origen el análisis de winning
          bid pierde sentido: no hay comparación entre compradores ni volumen
          suficiente, y con 0 sold todo sale en $0.00. Se ocultan la tira de
          promedio, el split por comprador, el promedio diario y el análisis
          de ventana de subasta. */}
      {!lockedOrigin && (
        <WinningBidStrip data={winningBid} loading={winningBidLoading} />
      )}

      {/* Auction Window Analysis commented out as requested */}
      {/* {!lockedOrigin && (
        <AuctionWindowAnalysis data={winningBid} loading={winningBidLoading} />
      )} */}


      {/* Fila de gráficos */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <ChartCard title="Leads per day" style={{ flex: '2 1 360px' }}>
          <LeadsByDayChart series={summary?.series} />
        </ChartCard>
        <ChartCard title="Composition" style={{ flex: '1 1 240px' }}>
          <CompositionChart summary={summary} />
        </ChartCard>
      </div>



      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {!lockedOrigin && (
          <ChartCard title="Leads by origin" hint="in the filtered range" style={{ flex: '1 1 360px' }}>
            <OriginBreakdown origins={summary?.by_origin} />
          </ChartCard>
        )}
        <ChartCard title="Rejection reasons" hint="in the filtered range" style={{ flex: '1 1 360px' }}>
          <RejectionReasons reasons={summary?.rejection_by_reason} />
        </ChartCard>
      </div>

      {loading && leadsResp.leads.length === 0 ? (
        <div className="card" style={{
          padding: 60, textAlign: 'center', color: 'var(--text-muted)',
        }}>
          <RefreshCw size={24} style={{ animation: 'spin 1.4s linear infinite' }} />
          <p style={{ marginTop: 12, fontSize: 13 }}>Loading…</p>
        </div>
      ) : (
        <LeadsTable leads={leadsResp.leads} />
      )}

      <Pagination
        page={leadsResp.page}
        totalPages={leadsResp.total_pages}
        total={leadsResp.total}
        limit={leadsResp.limit}
        onPageChange={setPage}
      />

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
